'use client';

import { IconCheck } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { StrapiImage } from '@/components/ui/strapi-image';
import { useCart } from '@/context/cart-context';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { cn, formatNumber } from '@/lib/utils';
import { Product } from '@/types/types';

export const SingleProduct = ({ product }: { product: Product }) => {
  const [activeThumbnail, setActiveThumbnail] = useState(
    strapiImage(product.images[0].url)
  );
  const { addToCart } = useCart();

  return (
    <div className="bg-neutral-50 dark:bg-gradient-to-b dark:from-neutral-900 dark:to-neutral-950 p-6 md:p-12 rounded-3xl border border-neutral-100 dark:border-neutral-800 shadow-sm dark:shadow-none">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            key={activeThumbnail}
            className="rounded-2xl relative overflow-hidden shadow-xl shadow-neutral-200/50 dark:shadow-none"
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 35,
            }}
          >
            <StrapiImage
              src={activeThumbnail}
              alt={product.name}
              width={800}
              height={800}
              className="rounded-2xl object-cover w-full aspect-square"
            />
          </motion.div>
          
          {product.images && product.images.length > 1 && (
            <div className="flex gap-4 justify-center items-center mt-6 flex-wrap">
              {product.images.map((image, index) => (
                <button
                  onClick={() => setActiveThumbnail(strapiImage(image.url))}
                  key={'product-image' + index}
                  className={cn(
                    'h-20 w-20 rounded-xl overflow-hidden transition-all duration-200 relative',
                    activeThumbnail === strapiImage(image.url)
                      ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-neutral-900 scale-105'
                      : 'ring-1 ring-neutral-200 dark:ring-neutral-800 hover:opacity-80'
                  )}
                  style={{
                    backgroundImage: `url(${strapiImage(image.url)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-neutral-900 dark:text-white tracking-tight">
            {product.name}
          </h2>
          <div className="mb-8">
            <span className="inline-flex items-center justify-center bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-lg font-semibold px-6 py-2 rounded-full shadow-md">
              ${formatNumber(product.price)}
            </span>
          </div>
          
          <p className="text-lg font-medium leading-relaxed mb-8 text-neutral-600 dark:text-neutral-400">
            {product.description}
          </p>

          <Divider />
          
          <ul className="flex flex-col gap-2 mb-10 mt-8">
            {product.perks &&
              product.perks.map((perk, index) => (
                <Step key={index}>{perk.text}</Step>
              ))}
          </ul>
          
          {product.plans && product.plans.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
                Available for
              </h3>
              <ul className="list-none flex gap-3 flex-wrap mb-10">
                {product.plans.map((plan, index) => (
                  <li
                    key={index}
                    className="bg-neutral-200/50 dark:bg-neutral-800 text-sm text-neutral-800 dark:text-neutral-200 px-4 py-2 rounded-full font-semibold border border-neutral-200 dark:border-neutral-700"
                  >
                    {plan.name}
                  </li>
                ))}
              </ul>
            </>
          )}

          {product.categories && product.categories.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
                Categories
              </h3>
              <ul className="flex gap-3 flex-wrap">
                {product.categories.map((category, idx) => (
                  <li
                    key={`category-${idx}`}
                    className="bg-neutral-100 dark:bg-neutral-800/50 text-sm text-neutral-600 dark:text-neutral-400 px-4 py-2 rounded-full font-medium"
                  >
                    {category.name}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Divider = () => {
  return (
    <div className="w-full h-px bg-neutral-200 dark:bg-neutral-800 my-2" />
  );
};

const Step = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex items-start justify-start gap-3 my-2">
      <div className="h-6 w-6 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
        <IconCheck className="h-4 w-4 [stroke-width:3px] text-primary" />
      </div>
      <div className="font-medium text-neutral-700 dark:text-neutral-300 text-base leading-relaxed">{children}</div>
    </div>
  );
};
